<div class="auth-shell">
  <div class="auth-card">
    <div class="auth-brand">
      <img src="assets/img/logo.png" class="auth-brand-mark" alt="">
      <span class="auth-brand-word">Fusion</span>
    </div>

    @switch (status) {
      @case ('redirecting') {
        <div class="status">
          <span class="spinner" aria-hidden="true"></span>
          <p class="status-text">{{ 'USER.REDIRECTING' | translate }}</p>
        </div>
      }
      @case ('error') {
        <div class="status">
          <lucide-icon name="triangle-alert" class="status-icon status-icon--error" size="28"></lucide-icon>
          <p class="status-text">{{ 'USER.REDIRECT_ERROR' | translate }}</p>
          <button type="button" class="btn-retry" (click)="retry()">
            <lucide-icon name="refresh-cw" size="16"></lucide-icon>
            <span>{{ 'USER.RETRY' | translate }}</span>
          </button>
        </div>
      }
      @case ('disabled') {
        <div class="status">
          <lucide-icon name="lock" class="status-icon status-icon--disabled" size="28"></lucide-icon>
          <p class="status-text">{{ 'USER.ACCOUNT_DISABLED' | translate }}</p>
        </div>
      }

      <!-- Local authentication. Only reachable when the server advertises local:true. -->
      @case ('password') {
        <div class="auth-body">
          <h2 class="auth-title">{{ 'AUTH.SIGN_IN_TITLE' | translate }}</h2>

          @if (errorKeys.length) {
            <div class="alert alert-danger">
              @for (key of errorKeys; track key) { <div>{{ key | translate }}</div> }
            </div>
          }

          <form (ngSubmit)="login()" #loginForm="ngForm">
            <div class="form-group">
              <label for="username">{{ 'USER.USERNAME' | translate }}</label>
              <input type="email" class="form-control" id="username" name="username"
                     [(ngModel)]="username" autocomplete="username" required #usernameField="ngModel">
            </div>
            <div class="form-group">
              <label for="password">{{ 'USER.PASSWORD' | translate }}</label>
              <input type="password" class="form-control" id="password" name="password"
                     [(ngModel)]="password" autocomplete="current-password" required #passwordField="ngModel">
            </div>
            <div class="auth-actions">
              <button type="submit" class="btn btn-primary" [disabled]="busy || !loginForm.form.valid">
                {{ 'AUTH.SIGN_IN' | translate }}
              </button>
            </div>
          </form>

          <div class="auth-links">
            <a routerLink="/forgot-password">{{ 'AUTH.FORGOT_LINK' | translate }}</a>
          </div>

          @if (ssoEnabled) {
            <div class="auth-links">
              <a (click)="signInWithSso()">{{ 'AUTH.SIGN_IN_WITH_SSO' | translate }}</a>
            </div>
          }
        </div>
      }

      @case ('mfa') {
        <div class="auth-body">
          <h2 class="auth-title">{{ 'AUTH.MFA_TITLE' | translate }}</h2>
          <p class="auth-lede">
            @if (emailCodeSent) {
              {{ 'AUTH.MFA_LEDE_EMAIL' | translate }}
            } @else {
              {{ 'AUTH.MFA_LEDE_TOTP' | translate }}
            }
          </p>

          @if (errorKeys.length) {
            <div class="alert alert-danger">
              @for (key of errorKeys; track key) { <div>{{ key | translate }}</div> }
            </div>
          }

          <form (ngSubmit)="submitMfa()" #mfaForm="ngForm">
            <div class="form-group">
              <label for="code">{{ 'AUTH.MFA_CODE' | translate }}</label>
              <input type="text" class="form-control auth-code-input" id="code" name="code"
                     [(ngModel)]="code" inputmode="numeric" autocomplete="one-time-code"
                     maxlength="6" required autofocus #codeField="ngModel">
            </div>
            @if (trustedDeviceDays > 0) {
              <div class="form-check auth-remember">
                <input type="checkbox" class="form-check-input" id="rememberDevice"
                       name="rememberDevice" [(ngModel)]="rememberDevice">
                <label class="form-check-label" for="rememberDevice">
                  {{ 'AUTH.REMEMBER_DEVICE' | translate:{ days: trustedDeviceDays } }}
                </label>
                <p class="auth-note">{{ 'AUTH.REMEMBER_DEVICE_HINT' | translate }}</p>
              </div>
            }

            <div class="auth-actions">
              <button type="button" class="btn btn-outline-secondary" [disabled]="busy" (click)="backToPassword()">
                {{ 'FUNCTION-BUTTONS.CANCEL' | translate }}
              </button>
              <button type="submit" class="btn btn-primary" [disabled]="busy || !mfaForm.form.valid">
                {{ 'AUTH.SIGN_IN' | translate }}
              </button>
            </div>
          </form>

          <div class="auth-links">
            @if (emailFallbackAvailable) {
              <a (click)="requestEmailCode()">
                @if (emailCodeSent) {
                  {{ 'AUTH.MFA_RESEND_EMAIL' | translate }}
                } @else {
                  {{ 'AUTH.MFA_USE_EMAIL' | translate }}
                }
              </a>
            }
            @if (recoveryAvailable) {
              <a (click)="useRecovery()">{{ 'AUTH.USE_RECOVERY_CODE' | translate }}</a>
            }
          </div>
        </div>
      }

      @case ('recovery') {
        <div class="auth-body">
          <h2 class="auth-title">{{ 'AUTH.RECOVERY_USE_TITLE' | translate }}</h2>
          <p class="auth-lede">{{ 'AUTH.RECOVERY_USE_LEDE' | translate }}</p>

          @if (errorKeys.length) {
            <div class="alert alert-danger">
              @for (key of errorKeys; track key) { <div>{{ key | translate }}</div> }
            </div>
          }

          <form (ngSubmit)="submitRecovery()" #recoveryForm="ngForm">
            <div class="form-group">
              <label for="recoveryCode">{{ 'AUTH.RECOVERY_CODE' | translate }}</label>
              <input type="text" class="form-control" id="recoveryCode" name="recoveryCode"
                     [(ngModel)]="recoveryCode" autocomplete="off" required #recoveryField="ngModel">
            </div>
            @if (trustedDeviceDays > 0) {
              <div class="form-check auth-remember">
                <input type="checkbox" class="form-check-input" id="rememberDeviceRecovery"
                       name="rememberDeviceRecovery" [(ngModel)]="rememberDevice">
                <label class="form-check-label" for="rememberDeviceRecovery">
                  {{ 'AUTH.REMEMBER_DEVICE' | translate:{ days: trustedDeviceDays } }}
                </label>
              </div>
            }

            <div class="auth-actions">
              <button type="button" class="btn btn-outline-secondary" [disabled]="busy" (click)="backToPassword()">
                {{ 'FUNCTION-BUTTONS.CANCEL' | translate }}
              </button>
              <button type="submit" class="btn btn-primary" [disabled]="busy || !recoveryForm.form.valid">
                {{ 'AUTH.SIGN_IN' | translate }}
              </button>
            </div>
          </form>
        </div>
      }

      @case ('enrol') {
        <div class="auth-body">
          <app-mfa-enrol [challenge]="challenge" [allowedMethods]="allowedMfaMethods"
                         (completed)="onEnrolled($event)"></app-mfa-enrol>
        </div>
      }
    }
  </div>
</div>
